/**
 * Post-login navigation, with the split-state failure made recoverable.
 *
 * The server has verified the credential and set the cookie BEFORE the router
 * callback runs, so a `navigate` that throws leaves the user authenticated and
 * still on `/login`. Both credential surfaces deliberately call `onSuccess`
 * outside their classified block so a throwing router is never reported as a
 * failed ceremony (`passkey-login.tsx`, `password-login.tsx`); the cost of that
 * correctness was an unhandled rejection and no affordance. This owns the other
 * half: catch it, keep the resolved target, and let the route offer a retry.
 *
 * Stateful, so it is a hook rather than logic in the route (style §3.2). The
 * route stays composition: it renders the panel and, when `stalled` is set, the
 * recovery message.
 */
import { useCallback, useRef, useState } from 'react';

import { resolveReturnTo, type ReturnToLocation } from '@/auth/return-to';

/**
 * The single navigate shape this hook uses.
 *
 * Deliberately not TanStack's `NavigateFn`: that type is generic over the route
 * tree and would drag the router's inference into a module whose only job is
 * error recovery. It is narrow enough that TanStack's `navigate` satisfies it
 * structurally, so the route needs no cast — and a local type keeps this
 * unit-testable without a router.
 */
type NavigateLike = (options: { to: string; search: Readonly<Record<string, unknown>> }) => unknown;

export interface PostLoginNavigation {
  /** Handed to the panel; resolves the target safely, then navigates. */
  readonly onSuccess: (target: ReturnToLocation) => void;
  /** The resolved target whose navigation failed, or `null` when all is well. */
  readonly stalled: ReturnToLocation | null;
  /** Retries `stalled`. No-op when nothing has failed. */
  readonly retry: () => void;
}

function isPromiseLike(value: unknown): value is PromiseLike<unknown> {
  return typeof (value as PromiseLike<unknown> | null)?.then === 'function';
}

function runNavigate(
  navigate: NavigateLike,
  safe: ReturnToLocation,
  apply: (next: ReturnToLocation | null) => void,
): void {
  try {
    const result = navigate({ to: safe.pathname, search: safe.search });
    /* A router may fail either way, and the two are not interchangeable:
       `try` alone misses a rejected promise, `.catch` alone misses a
       synchronous throw. Both arms land on the same recovery. */
    if (isPromiseLike(result)) {
      result.then(
        () => {
          apply(null);
        },
        () => {
          apply(safe);
        },
      );
      return;
    }
    apply(null);
  } catch {
    apply(safe);
  }
}

export function usePostLoginNavigation(navigate: NavigateLike): PostLoginNavigation {
  const [stalled, setStalled] = useState<ReturnToLocation | null>(null);
  const generation = useRef(0);

  /*
   * Takes an ALREADY-resolved target. `resolveReturnTo` is applied once, on the
   * way in, so a retry cannot re-resolve — re-running it would be wasted work,
   * and skipping it on the first call would navigate to unvalidated history
   * state. One resolution, reused verbatim.
   */
  const go = useCallback(
    (safe: ReturnToLocation): void => {
      const seq = generation.current + 1;
      generation.current = seq;
      runNavigate(navigate, safe, (next) => {
        if (seq !== generation.current) return;
        setStalled(next);
      });
    },
    [navigate],
  );

  const onSuccess = useCallback(
    (target: ReturnToLocation): void => {
      go(resolveReturnTo(target));
    },
    [go],
  );

  const retry = useCallback((): void => {
    if (stalled === null) return;
    go(stalled);
  }, [go, stalled]);

  return { onSuccess, stalled, retry };
}
